import { FC } from "react";
import { useSearchPage } from "@/pages/Search/SearchPage.hooks";
import { MenuButtonView } from "@/components/MenuButtonView";
import { ChatBubbleIcon, ChevronUpIcon } from "@radix-ui/react-icons";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import { SearchResultItem } from "./SearchResultItem";
import { MobileView, PcView } from "@/components/DisplayBreakpointView";
import { GeneralTemplate } from "@/components/templates/GeneralTemplate";
import { AlertMessageDialog } from "@/components/MessageDialog";
import { Button } from "@/components/ui/button";
import { ChatDialog } from "./ChatDialog/ChatDialog";
import { AppVersion } from "@/components/AppVersion";
import { Badge } from "@/components/ui/badge";
import { KeyphraseInput } from "./KeyphraseInput";
import { FilterView } from "./FilterView";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ChevronDownIcon, FilterIcon } from "lucide-react";
import { cn } from "@/utils/tailwind/utils";
import { Toaster } from "@/components/ui/toaster";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { FormField } from "@/components/ui/form";
import { HelpTooltip } from "./HelpTooltip";
import { SystemHeader } from "@/components/SystemHeader/SystemHeader";
import { SearchModelView } from "@/components/SearchModelView/SearchModelView";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import TriangleRight from "../../assets/images/triangle-right.svg";
import TriangleLeft from "../../assets/images/triangle-left.svg";

export const SearchPage: FC = () => {
  const hooks = useSearchPage();
  const {
    t,
    loading,
    onSubmit,
    result,
    apiErrorMessage,
    apiErrorDialogOpen,
    setApiErrorDialogOpen,
    onClickShowChatDialog,
    onChangeKeyphrase,
    filterOpen,
    updateFilterOpen,
    onClickFilterTrigger,
    filterTriggerRef,
    scrollAnchorRef,
    tabCategories,
    setCurrentTab,
    currentTab,
    filteredSearchResults,
    onClickBookmark,
    bookmarksSet,
    onClickGood,
    onChangeLanguage,
    totalDocumentsGood,
    goodDocumentIdsSet,
    form,
    onSelectSort,
    sortOptions,
    onClickDocumentTitle,
    searchKeyphraseHelpOpen,
    onClickOpenSearchKeyphraseHelp,
    onClickCloseSearchKeyphraseHelp,
    searchModelOpen,
    onClickOpenSearchModel,
    onClickCloseSearchModel,
    onSelectModel,
    firLink,
    tabScrollToPrev,
    tabScrollAnchorRef,
    tabScrollToNext,
    parentScrollAnchorRef,
    hasTabScroll,
  } = hooks;

  const sortSelectBox = (
    <FormField
      control={form.control}
      name={"sort"}
      render={({ field }) => {
        return (
          <Select value={field.value} onValueChange={onSelectSort}>
            <SelectTrigger className="flex h-[28px] w-fit flex-row gap-2.5">
              <SelectValue placeholder={t("sort_by")} />
              <ChevronDownIcon className="h-4 w-4" />
            </SelectTrigger>
            <SelectContent>
              {sortOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        );
      }}
    />
  );

  const resultView = (
    <div className="flex flex-col items-center py-4">
      <div className="flex flex-col gap-y-4">
        {result && result.items.length >= 2 && <div className="flex justify-end">{sortSelectBox}</div>}
        {result?.items.length === 0 && (
          <div className="space-y-2 px-4 pt-2">
            <p className="text-lg">{t("search_empty_title")}</p>
            <p className="text-sm">{t("search_empty_try_next")}</p>
            <ul className="ml-2 list-inside list-disc text-sm">
              <li>{t("search_empty_different_keyword")}</li>
              <li>{t("search_empty_general_keywords")}</li>
              <li>{t("search_empty_reduce_keywords")}</li>
              <li>{t("search_empty_check_spell")}</li>
            </ul>
          </div>
        )}
        {filteredSearchResults.map((data, index) => {
          return (
            <SearchResultItem
              key={index}
              data={data}
              bookmarksSet={bookmarksSet}
              onClickBookmark={onClickBookmark}
              totalDocumentsGood={totalDocumentsGood}
              goodDocumentIdsSet={goodDocumentIdsSet}
              onClickGood={onClickGood}
              onClickDocumentTitle={onClickDocumentTitle}
            />
          );
        })}
      </div>
    </div>
  );

  const hasSuggestions = (result?.querySuggestions ?? []).length > 0;
  const hasTabs = tabCategories.length >= 2;
  const hasResultHeader = hasTabs || hasSuggestions;
  const relatedKeywordsView = (
    <>
      {hasResultHeader && (
        <div className="flex flex-col bg-gray-100">
          {hasSuggestions && (
            <>
              <MobileView>
                <div className="bg-gray-100 px-4 pt-2.5">
                  <p className="text-sm">{t("search_related_keywords")}</p>
                </div>
              </MobileView>
              <div className="flex flex-col bg-gray-100">
                <div className="flex h-14 items-center gap-x-4">
                  <ScrollArea className="flex-1">
                    <div className="flex h-full w-max items-center gap-x-4 px-4">
                      <PcView>
                        <p className="text-sm">{t("search_related_keywords")}</p>
                      </PcView>
                      {result?.querySuggestions?.map((data, index) => (
                        <Button
                          key={index}
                          variant="ghost"
                          type="button"
                          className="px-0 hover:bg-accent"
                          onClick={() => {
                            onChangeKeyphrase(data);
                            onSubmit();
                          }}
                        >
                          <Badge variant="secondary" className="h-6 border-gray-600 bg-white text-xs text-black">
                            {data}
                          </Badge>
                        </Button>
                      ))}
                    </div>
                    <ScrollBar orientation="horizontal" className="hidden" />
                  </ScrollArea>
                </div>
              </div>
            </>
          )}
          {hasTabs && (
            <div className={"flex flex-row items-end"} ref={parentScrollAnchorRef}>
              <ScrollArea>
                <div className={"flex w-max"} ref={tabScrollAnchorRef}>
                  <Tabs
                    defaultValue={currentTab}
                    orientation={"horizontal"}
                    onValueChange={(value) => setCurrentTab(value as string | "all")}
                    className={`mx-3 ${!hasSuggestions && "mt-3"}`}
                  >
                    <TabsList>
                      {tabCategories.map((category) => {
                        return (
                          <TabsTrigger key={category.value} value={category.value}>
                            {category.label} ({category.count})
                          </TabsTrigger>
                        );
                      })}
                    </TabsList>
                  </Tabs>
                </div>
                <ScrollBar orientation="horizontal" className="hidden" />
              </ScrollArea>
              {hasTabScroll && (
                <div className={"mb-1 flex flex-row"}>
                  <Button variant={"outline"} className={"size-8 p-0"} onClick={tabScrollToPrev}>
                    <img className={"h-4 w-4"} src={TriangleLeft} alt={""} />
                  </Button>
                  <Button variant={"outline"} className={"size-8 p-0"} onClick={tabScrollToNext}>
                    <img className={"h-4 w-4"} src={TriangleRight} alt={""} />
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </>
  );

  const filterAccordionOpenButtion = (
    <MobileView>
      <Button
        variant={filterOpen ? "ghost" : "default"}
        className={cn("h-10 w-10 p-0", filterOpen ? "border-[1px] border-gray-200 bg-white" : "")}
        onClick={onClickFilterTrigger}
      >
        <FilterIcon className="h-4 w-4" />
      </Button>
    </MobileView>
  );

  const filterAccordion = (
    <MobileView>
      <Accordion type="single" collapsible onValueChange={updateFilterOpen} value={filterOpen ? "filter" : ""}>
        <AccordionItem value="filter" className="border-none">
          <AccordionTrigger className="hidden" ref={filterTriggerRef} />
          <AccordionContent className="p-0">
            <FilterView {...hooks} onClickReferenceButton={onClickOpenSearchModel} />
            <Button
              variant="ghost"
              className="h-7 w-full gap-x-2 rounded-none border-y-[1px] border-gray-200 bg-white"
              onClick={onClickFilterTrigger}
            >
              <ChevronUpIcon className="h-4 w-4" />
              <p className="text-sm">{t("close")}</p>
            </Button>
          </AccordionContent>
        </AccordionItem>
      </Accordion>
    </MobileView>
  );

  return (
    <>
      <PcView>
        <GeneralTemplate
          header={
            <>
              <SystemHeader />
              <div className="flex w-full flex-col items-center border-b-[1px] border-gray-200 bg-white">
                <div className="flex h-14 w-full flex-row items-center pl-4 pr-2">
                  <p className="text-base font-normal">{t("app_title")}</p>
                  <div className="flex-1 px-4">
                    {result != undefined && (
                      <KeyphraseInput
                        {...hooks}
                        tooltip={
                          <div className="flex items-center">
                            <HelpTooltip
                              className="flex w-[450px] flex-col gap-y-4 p-4 sm:max-w-[calc(100vw-336px)]"
                              title={<span className="text-lg font-semibold">{t("search_keyphrase_help_title")}</span>}
                              open={searchKeyphraseHelpOpen}
                              onClickOpen={onClickOpenSearchKeyphraseHelp}
                              onClickClose={onClickCloseSearchKeyphraseHelp}
                              position={"bottom"}
                              align={"end"}
                              alignOffset={-13}
                            >
                              <ScrollArea>
                                <div className="flex max-h-[calc(100vh-181px)] flex-col gap-y-4">
                                  {(
                                    t("search_keyphrase_help_contents", { returnObjects: true }) as {
                                      title: string;
                                      description: string;
                                      example: string;
                                    }[]
                                  ).map((content, index) => (
                                    <div className="flex flex-col" key={index}>
                                      <span className="text-sm font-semibold">{content.title}</span>
                                      <span className="text-sm">{content.description}</span>
                                      <div className="rounded bg-zinc-100 px-2 py-px">
                                        <span className="text-sm">{content.example}</span>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </ScrollArea>
                            </HelpTooltip>
                          </div>
                        }
                      />
                    )}
                  </div>
                  <MenuButtonView onChangeLanguage={onChangeLanguage} firLink={firLink} />
                </div>
              </div>
            </>
          }
          loading={loading}
          toast={<Toaster toastClassName="-top-2 h-12 px-4" />}
          dialog={
            <>
              <Dialog open={searchModelOpen} onOpenChange={onClickCloseSearchModel}>
                <DialogContent
                  className="fixed flex h-[calc(100%-32px)] rounded-md"
                  onOpenAutoFocus={(e) => e.preventDefault()}
                >
                  <SearchModelView onClickSelector={(model) => onSelectModel(model)} />
                </DialogContent>
              </Dialog>
              <AlertMessageDialog open={apiErrorDialogOpen} message={apiErrorMessage} setOpen={setApiErrorDialogOpen} />
            </>
          }
        >
          <div className="flex h-[calc(100svh-108px)] w-full flex-row bg-gray-100">
            {result == undefined && (
              <div className="flex-1">
                <div className="mt-32 flex justify-center px-6">
                  <KeyphraseInput
                    shadow={true}
                    {...hooks}
                    tooltip={
                      <HelpTooltip
                        className="flex w-[450px] flex-col gap-y-4 p-4 sm:max-w-[calc(100vw-336px)]"
                        title={<span className="text-lg font-semibold">{t("search_keyphrase_help_title")}</span>}
                        open={searchKeyphraseHelpOpen}
                        onClickOpen={onClickOpenSearchKeyphraseHelp}
                        onClickClose={onClickCloseSearchKeyphraseHelp}
                        position={"bottom"}
                        align={"end"}
                        alignOffset={-13}
                      >
                        <ScrollArea>
                          <div className="flex max-h-[calc(100vh-361px)] flex-col gap-y-4">
                            {(
                              t("search_keyphrase_help_contents", { returnObjects: true }) as {
                                title: string;
                                description: string;
                                example: string;
                              }[]
                            ).map((content, index) => (
                              <div className="flex flex-col" key={index}>
                                <span className="text-sm font-semibold">{content.title}</span>
                                <span className="text-sm">{content.description}</span>
                                <div className="rounded bg-zinc-100 px-2 py-px">
                                  <span className="text-sm">{content.example}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </HelpTooltip>
                    }
                  />
                </div>
              </div>
            )}
            {result != undefined && (
              <ScrollArea key={result.queryId} className="bg-white">
                <div className="w-[calc(100vw-288px)]">
                  {relatedKeywordsView}
                  {resultView}
                </div>
              </ScrollArea>
            )}
            <div className="w-[288px] drop-shadow-md">
              <FilterView {...hooks} onClickReferenceButton={onClickOpenSearchModel} />
            </div>
          </div>

          {window.envData.env !== "prd" && (
            <Button
              className="absolute bottom-4 left-4 h-12 w-12 rounded-full bg-black"
              onClick={onClickShowChatDialog}
            >
              <ChatBubbleIcon className="h-4 w-4" />
            </Button>
          )}

          <div className="absolute bottom-0 right-[288px] h-12 w-24">
            <AppVersion />
          </div>
        </GeneralTemplate>
      </PcView>

      <MobileView>
        <GeneralTemplate
          header={
            <>
              <SystemHeader />
              <div className="flex w-full flex-col items-center border-b-[1px] border-gray-200 bg-white">
                <div className="flex h-14 w-full flex-row items-center pl-4 pr-2">
                  <p className="text-base font-normal">{t("app_title")}</p>
                  <div className="flex-1" />
                  <MenuButtonView firLink={firLink} />
                </div>
                {result != undefined && (
                  <div className="flex w-full flex-row justify-center gap-x-2 px-6 pb-2">
                    <KeyphraseInput
                      {...hooks}
                      tooltip={
                        <HelpTooltip
                          className="flex w-[calc(100vw-96px)] flex-col gap-y-4 p-4"
                          title={<span className="text-lg font-semibold">{t("search_keyphrase_help_title")}</span>}
                          open={searchKeyphraseHelpOpen}
                          onClickOpen={onClickOpenSearchKeyphraseHelp}
                          onClickClose={onClickCloseSearchKeyphraseHelp}
                          position={"bottom"}
                          align={"end"}
                          alignOffset={-13}
                        >
                          <ScrollArea>
                            <div className="flex max-h-[calc(100vh-231px)] flex-col gap-y-4">
                              {(
                                t("search_keyphrase_help_contents", { returnObjects: true }) as {
                                  title: string;
                                  description: string;
                                  example: string;
                                }[]
                              ).map((content, index) => (
                                <div className="flex flex-col" key={index}>
                                  <span className="text-sm font-semibold">{content.title}</span>
                                  <span className="text-sm">{content.description}</span>
                                  <div className="rounded bg-zinc-100 px-2 py-px">
                                    <span className="text-sm">{content.example}</span>
                                  </div>
                                </div>
                              ))}
                            </div>
                          </ScrollArea>
                        </HelpTooltip>
                      }
                    />
                    {filterAccordionOpenButtion}
                  </div>
                )}
              </div>
            </>
          }
          loading={loading}
          toast={<Toaster toastClassName="-top-2 h-12 px-4" />}
          dialog={
            <>
              <Dialog open={searchModelOpen} onOpenChange={onClickCloseSearchModel}>
                <DialogContent
                  className="fixed flex h-[calc(100%-16px)] w-[calc(100%-16px)] rounded-md"
                  onOpenAutoFocus={(e) => e.preventDefault()}
                >
                  <SearchModelView onClickSelector={(model) => onSelectModel(model)} />
                </DialogContent>
              </Dialog>
              <AlertMessageDialog open={apiErrorDialogOpen} message={apiErrorMessage} setOpen={setApiErrorDialogOpen} />
            </>
          }
        >
          <div className="flex h-full w-full flex-col bg-gray-100">
            {result == undefined && (
              <>
                <div className="mb-2.5 mt-16 flex flex-row justify-center gap-x-2 px-6">
                  <KeyphraseInput
                    shadow={true}
                    {...hooks}
                    tooltip={
                      <HelpTooltip
                        className="flex w-[calc(100vw-96px)] flex-col gap-y-4 p-4"
                        title={<span className="text-lg font-semibold">{t("search_keyphrase_help_title")}</span>}
                        open={searchKeyphraseHelpOpen}
                        onClickOpen={onClickOpenSearchKeyphraseHelp}
                        onClickClose={onClickCloseSearchKeyphraseHelp}
                        position={"bottom"}
                        align={"end"}
                        alignOffset={-13}
                      >
                        <ScrollArea>
                          <div className="flex max-h-[calc(100vh-301px)] flex-col gap-y-4">
                            {(
                              t("search_keyphrase_help_contents", { returnObjects: true }) as {
                                title: string;
                                description: string;
                                example: string;
                              }[]
                            ).map((content, index) => (
                              <div className="flex flex-col" key={index}>
                                <span className="text-sm font-semibold">{content.title}</span>
                                <span className="text-sm">{content.description}</span>
                                <div className="rounded bg-zinc-100 px-2 py-px">
                                  <span className="text-sm">{content.example}</span>
                                </div>
                              </div>
                            ))}
                          </div>
                        </ScrollArea>
                      </HelpTooltip>
                    }
                  />
                  {filterAccordionOpenButtion}
                </div>
                <ScrollArea className="h-[calc(100svh-222px)]">
                  <div className="w-svw pb-12">{filterAccordion}</div>
                </ScrollArea>
              </>
            )}
            {result != undefined && (
              <ScrollArea key={result.queryId} className="h-[calc(100svh-156px)] bg-white">
                <div className="w-svw pb-12">
                  <div ref={scrollAnchorRef} />
                  {filterAccordion}
                  {relatedKeywordsView}
                  {resultView}
                </div>
              </ScrollArea>
            )}
          </div>

          {window.envData.env !== "prd" && (
            <Button
              className="absolute bottom-4 left-4 h-12 w-12 rounded-full bg-black"
              onClick={onClickShowChatDialog}
            >
              <ChatBubbleIcon className="h-4 w-4" />
            </Button>
          )}
          <AppVersion />
        </GeneralTemplate>
      </MobileView>

      <ChatDialog {...hooks} />
    </>
  );
};
